import engine from "./proteinEngine";

// Scoped initialization is safe across React Strict Mode and route changes.
export function initProteinCalculator(root) {
  const controller = new AbortController();
  const listen = (element, event, handler) => element.addEventListener(event, handler, {signal: controller.signal});
  let disposed = false;
  const dialog = root.querySelector('#protein-dialog');
  const form = root.querySelector('#pc-form');
  const layout = dialog.querySelector('.pc-layout');
  const inline = root.querySelector('#protein-inline');
  const byId = id => root.querySelector('#' + id);
  let step = 0;
  let visited = 0;
  let returnFocus = null;
  let returnToInline = false;
  let autoTimer;
  let seenInMemory = false;
  form.reset();
  const visitKey = 'lenis-protein-calculator-seen-v1';

  function addNumbers(id, start, end, unit) {
    const fragment = document.createDocumentFragment();
    for (let value = start; value <= end; value++) {
      const option = document.createElement('option');
      option.value = String(value);
      option.textContent = value + ' ' + unit;
      fragment.append(option);
    }
    byId(id).replaceChildren(byId(id).options[0], fragment);
  }
  addNumbers('pc-weight-kg', 1, 200, 'kg');
  addNumbers('pc-weight-lb', 1, 300, 'lb');
  addNumbers('pc-feet', 2, 8, 'ft');
  addNumbers('pc-inches', 0, 11, 'in');

  function addChoices(containerId, name, choices, type) {
    const container = byId(containerId);
    container.replaceChildren();
    choices.forEach(([value, label, detail]) => {
      const wrapper = document.createElement('label');
      wrapper.className = 'pc-choice';
      const input = document.createElement('input');
      input.type = type;
      input.name = name;
      input.value = value;
      input.id = 'pc-' + name + '-' + value;
      const copy = document.createElement('span');
      if (detail) {
        const title = document.createElement('strong');
        const subtitle = document.createElement('small');
        title.textContent = label;
        subtitle.textContent = detail;
        copy.append(title, subtitle);
      } else copy.textContent = label;
      wrapper.append(input, copy);
      container.append(wrapper);
    });
  }
  addChoices('pc-goal-options', 'goals', engine.goals, 'checkbox');
  addChoices('pc-activity-options', 'activity', Object.entries(engine.activities).map(([value, item]) => [value, item.label, item.detail]), 'radio');
  addChoices('pc-fitness-options', 'fitnessGoals', engine.fitnessGoals, 'checkbox');
  addChoices('pc-symptom-options', 'symptoms', engine.symptoms, 'checkbox');

  const selected = name => [...form.querySelectorAll('input[name="' + name + '"]:checked')].map(input => input.value);
  function answers() {
    return {
      name: byId('pc-name').value, sex: byId('pc-sex').value,
      ageBand: byId('pc-age-band').value, age: byId('pc-age').value,
      heightUnit: selected('heightUnit')[0], heightCm: byId('pc-height-cm').value,
      feet: byId('pc-feet').value, inches: byId('pc-inches').value,
      weightUnit: selected('weightUnit')[0], weightKg: byId('pc-weight-kg').value,
      weightLb: byId('pc-weight-lb').value, goals: selected('goals'),
      activity: selected('activity')[0] || '', fitnessGoals: selected('fitnessGoals'),
      diet: byId('pc-diet').value, symptoms: selected('symptoms')
    };
  }
  function fieldControl(field) {
    const ids = {name: 'pc-name', sex: 'pc-sex', ageBand: 'pc-age-band', age: 'pc-age', diet: 'pc-diet'};
    if (ids[field]) return byId(ids[field]);
    if (field === 'height') return byId(selected('heightUnit')[0] === 'imperial' ? 'pc-feet' : 'pc-height-cm');
    if (field === 'weight') return byId(selected('weightUnit')[0] === 'lb' ? 'pc-weight-lb' : 'pc-weight-kg');
    return form.querySelector('input[name="' + field + '"]');
  }
  function clearErrors() {
    byId('pc-errors').hidden = true;
    byId('pc-error-list').replaceChildren();
    form.querySelectorAll('[aria-invalid]').forEach(control => {
      control.removeAttribute('aria-invalid');
      const description = (control.getAttribute('aria-describedby') || '').split(' ').filter(id => id && id !== 'pc-errors').join(' ');
      if (description) control.setAttribute('aria-describedby', description);
      else control.removeAttribute('aria-describedby');
    });
  }
  function showErrors(errors) {
    clearErrors();
    errors.forEach(error => {
      const control = fieldControl(error.field);
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = error.message;
      listen(button, 'click', () => control?.focus());
      li.append(button);
      byId('pc-error-list').append(li);
      if (control) {
        control.setAttribute('aria-invalid', 'true');
        control.setAttribute('aria-describedby', [control.getAttribute('aria-describedby'), 'pc-errors'].filter(Boolean).join(' '));
      }
    });
    byId('pc-errors').hidden = false;
    fieldControl(errors[0].field)?.focus();
  }
  function updateCounters() {
    byId('pc-goals-count').textContent = selected('goals').length + ' selected · at least 1';
    byId('pc-fitness-count').textContent = selected('fitnessGoals').length + ' of 4 selected · at least 2';
    byId('pc-symptoms-count').textContent = selected('symptoms').length + ' selected · maximum 3';
  }
  function syncAge() {
    const under18 = byId('pc-age-band').value === 'under18';
    byId('pc-exact-age-field').hidden = under18;
    byId('pc-age').disabled = under18;
    byId('pc-under18-note').hidden = !under18;
    updateNext();
  }
  function updateNext() {
    const label = byId('pc-age-band').value === 'under18' ? 'View guidance' : step === 3 ? 'Show my report' : 'Continue';
    byId('pc-next').replaceChildren(document.createTextNode(label + ' '));
    const arrow = document.createElement('span');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    byId('pc-next').append(arrow);
  }
  function showStep(next, focus) {
    step = next;
    visited = Math.max(visited, next);
    clearErrors();
    byId('pc-form').hidden = false;
    byId('pc-progress').hidden = false;
    byId('pc-report').hidden = true;
    byId('pc-guidance').hidden = true;
    form.querySelectorAll('[data-pc-page]').forEach(page => {page.hidden = Number(page.dataset.pcPage) !== next;});
    root.querySelectorAll('[data-pc-step]').forEach(button => {
      const index = Number(button.dataset.pcStep);
      button.disabled = index > visited;
      if (index === next) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    });
    byId('pc-back').hidden = next === 0;
    byId('pc-step-label').textContent = 'Step ' + (next + 1) + ' of 4';
    updateNext();
    if (focus) form.querySelector('[data-pc-page="' + next + '"] h3').focus();
  }

  listen(form, 'submit', event => {
    event.preventDefault();
    const data = answers();
    if (data.ageBand === 'under18') {renderReport(engine.calculate(data)); return;}
    const errors = engine.validate(data, step);
    if (errors.length) {showErrors(errors); return;}
    if (step < 3) showStep(step + 1, true);
    else {
      const report = engine.calculate(data);
      if (report.status === 'invalid') {
        showStep(report.errors[0].step, false);
        showErrors(report.errors.filter(error => error.step === step));
      } else renderReport(report);
    }
  });
  listen(byId('pc-back'), 'click', () => showStep(Math.max(0, step - 1), true));
  root.querySelectorAll('[data-pc-step]').forEach(button => listen(button, 'click', () => {
    const next = Number(button.dataset.pcStep);
    if (next > step) {
      const errors = engine.validate(answers(), step);
      if (errors.length) {showErrors(errors); return;}
    }
    showStep(next, true);
  }));
  listen(form, 'input', clearErrors);
  listen(byId('pc-age-band'), 'change', syncAge);

  function syncUnitVisibility() {
    const imperialHeight = selected('heightUnit')[0] === 'imperial';
    const imperialWeight = selected('weightUnit')[0] === 'lb';
    byId('pc-height-metric').hidden = imperialHeight;
    byId('pc-height-imperial').hidden = !imperialHeight;
    byId('pc-height-cm').disabled = imperialHeight;
    byId('pc-feet').disabled = !imperialHeight;
    byId('pc-inches').disabled = !imperialHeight;
    byId('pc-weight-metric').hidden = imperialWeight;
    byId('pc-weight-imperial').hidden = !imperialWeight;
    byId('pc-weight-kg').disabled = imperialWeight;
    byId('pc-weight-lb').disabled = !imperialWeight;
  }
  listen(form, 'change', event => {
    const input = event.target;
    clearErrors();
    if (input.name === 'heightUnit') {
      if (input.value === 'imperial' && byId('pc-height-cm').value) {
        const totalInches = Math.round(Number(byId('pc-height-cm').value) / engine.INCH_TO_CM);
        byId('pc-feet').value = String(Math.floor(totalInches / 12));
        byId('pc-inches').value = String(totalInches % 12);
      } else if (input.value === 'metric' && byId('pc-feet').value && byId('pc-inches').value !== '') {
        byId('pc-height-cm').value = String(Math.round((Number(byId('pc-feet').value) * 12 + Number(byId('pc-inches').value)) * engine.INCH_TO_CM * 10) / 10);
      }
      syncUnitVisibility();
    }
    if (input.name === 'weightUnit') {
      byId('pc-weight-unit-note').textContent = 'Kilograms: 1–200 · Pounds: 1–300. Unit conversions are rounded to the nearest whole unit.';
      if (input.value === 'lb' && byId('pc-weight-kg').value) {
        const pounds = Math.round(Number(byId('pc-weight-kg').value) / engine.LB_TO_KG);
        byId('pc-weight-lb').value = pounds <= 300 ? String(pounds) : '';
        if (pounds > 300) byId('pc-weight-unit-note').textContent = 'Your current weight is above this list’s 300 lb limit. Keep using kilograms, or choose a valid pounds value.';
      } else if (input.value === 'kg' && byId('pc-weight-lb').value) {
        byId('pc-weight-kg').value = String(Math.round(Number(byId('pc-weight-lb').value) * engine.LB_TO_KG));
      }
      syncUnitVisibility();
    }
    if (input.name === 'goals' && input.checked && ['gain', 'loss', 'maintain'].includes(input.value)) {
      form.querySelectorAll('input[name="goals"]').forEach(other => {if (other !== input && ['gain', 'loss', 'maintain'].includes(other.value)) other.checked = false;});
    }
    if (input.name === 'symptoms') {
      byId('pc-selection-note').hidden = true;
      if (input.checked && ['none', 'private'].includes(input.value)) {
        form.querySelectorAll('input[name="symptoms"]').forEach(other => {if (other !== input) other.checked = false;});
      } else if (input.checked) {
        form.querySelectorAll('input[name="symptoms"]').forEach(other => {if (['none', 'private'].includes(other.value)) other.checked = false;});
        if (selected('symptoms').length > 3) {
          input.checked = false;
          byId('pc-selection-note').textContent = 'Choose up to three. Deselect an answer before choosing another.';
          byId('pc-selection-note').hidden = false;
        }
      }
    }
    updateCounters();
  });

  function renderReport(report) {
    clearErrors();
    byId('pc-form').hidden = true;
    byId('pc-progress').hidden = true;
    if (report.status !== 'ready') {
      byId('pc-report').hidden = true;
      byId('pc-guidance').hidden = false;
      const under18 = report.status === 'under18';
      byId('pc-guidance-title').textContent = under18 ? 'A growing body needs its own approach.' : 'A more individual approach.';
      byId('pc-guidance-copy').textContent = under18 ? 'Your nutrition needs change as you grow. This adult calculator does not generate calorie, weight-loss or macro targets for under-18s. A registered dietitian or healthcare professional can help you plan balanced meals that support growth and activity.' : report.message;
      byId('pc-guidance-title').focus();
      return;
    }
    byId('pc-guidance').hidden = true;
    byId('pc-report').hidden = false;
    byId('pc-report-title').textContent = 'Hello ' + report.name + ', here’s your report';
    byId('pc-protein').textContent = String(report.protein);
    byId('pc-carbs').textContent = String(report.carbs);
    byId('pc-fat').textContent = String(report.fat);
    byId('pc-calories').textContent = new Intl.NumberFormat('en-GB').format(report.calories);
    byId('pc-report-summary').textContent = report.kg.toFixed(1) + ' kg · ' + report.cm.toFixed(1) + ' cm · Age ' + report.age + ' · ' + report.activityLabel + '. ' + report.adjustmentLabel + '.';
    const notes = byId('pc-report-notes');
    notes.replaceChildren();
    report.notes.forEach(note => {
      const paragraph = document.createElement('p');
      paragraph.className = 'pc-report-note';
      paragraph.textContent = note;
      notes.append(paragraph);
    });
    const dietNotes = {
      vegan: 'For a vegan routine, include a variety of plant protein foods across your day. Egg White Protein contains egg and is not vegan.',
      vegetarian: 'For a vegetarian routine, protein can come from a mix of plant foods and any eggs or dairy you choose to eat. Check ingredient and allergen labels.',
      flexitarian: 'For a flexitarian routine, combine the plant and animal protein foods you enjoy. Your daily total includes protein from every meal and snack.',
      anything: 'Build your daily total from the protein foods you enjoy across meals and snacks. A supplement is an optional convenience.'
    };
    byId('pc-diet-note').textContent = dietNotes[report.diet];
    byId('pc-method-details').textContent = 'Resting energy: ' + report.resting + ' kcal/day using 10 × weight (kg) + 6.25 × height (cm) − 5 × age + ' + (answers().sex === 'male' ? '5' : '(−161)') + '. Activity multiplier: ' + report.activityFactor + '. Estimated maintenance: ' + report.maintenance + ' kcal/day. Protein calculation: ' + report.kg.toFixed(1) + ' kg × ' + report.proteinFactor + ' g/kg/day. ' + report.adjustmentLabel + '.';
    byId('pc-report-title').focus();
  }
  listen(byId('pc-edit'), 'click', () => showStep(0, true));
  listen(byId('pc-guidance-edit'), 'click', () => showStep(0, true));
  listen(byId('pc-reset'), 'click', () => {
    form.reset();
    visited = 0;
    byId('pc-report-notes').replaceChildren();
    ['pc-report-title', 'pc-report-summary', 'pc-method-details', 'pc-diet-note', 'pc-protein', 'pc-carbs', 'pc-fat', 'pc-calories'].forEach(id => {byId(id).textContent = '';});
    byId('pc-selection-note').hidden = true;
    byId('pc-weight-unit-note').textContent = 'Kilograms: 1–200 · Pounds: 1–300. Very low adult weights need individual assessment.';
    syncAge(); syncUnitVisibility(); updateCounters(); showStep(0, true);
  });

  function wasSeen() {
    if (seenInMemory) return true;
    for (const storage of ['localStorage', 'sessionStorage']) {
      try {if (window[storage].getItem(visitKey) === 'shown') return true;} catch (_) { /* Storage may be blocked. */ }
    }
    return false;
  }
  function markSeen() {
    seenInMemory = true;
    for (const storage of ['localStorage', 'sessionStorage']) {
      try {window[storage].setItem(visitKey, 'shown'); return;} catch (_) { /* Try session storage next. */ }
    }
  }
  function openCalculator() {
    if (dialog.open || document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]')) return;
    returnFocus = document.activeElement;
    returnToInline = inline.contains(layout) && !inline.hidden;
    inline.hidden = true;
    dialog.append(layout);
    byId('navigation').classList.remove('open');
    byId('menu').setAttribute('aria-expanded', 'false');
    byId('menu').setAttribute('aria-label', 'Open menu');
    dialog.showModal();
    document.body.classList.add('pc-modal-open');
    markSeen();
    clearTimeout(autoTimer);
  }
  function openInlineCalculator() {
    clearTimeout(autoTimer);
    markSeen();
    inline.append(layout);
    inline.hidden = false;
    const target = byId('pc-report').hidden ? byId('pc-guidance').hidden ? form.querySelector('[data-pc-page="' + step + '"] h3') : byId('pc-guidance-title') : byId('pc-report-title');
    target.focus();
  }
  function collapseInline() {
    inline.hidden = true;
    root.querySelector('[data-protein-calculator]').focus({preventScroll: true});
  }
  root.querySelectorAll('[data-protein-calculator]').forEach(button => {
    button.setAttribute('aria-controls', 'protein-inline');
    button.removeAttribute('aria-haspopup');
    button.setAttribute('aria-expanded', 'false');
    listen(button, 'click', () => {openInlineCalculator(); button.setAttribute('aria-expanded', 'true');});
  });
  listen(byId('pc-open-popup'), 'click', openCalculator);
  listen(byId('pc-collapse'), 'click', () => {
    collapseInline();
    root.querySelector('[data-protein-calculator]').setAttribute('aria-expanded', 'false');
  });
  root.querySelectorAll('[data-pc-close]').forEach(button => listen(button, 'click', () => {
    if (dialog.open) dialog.close();
    else if (inline.contains(layout)) {
      collapseInline();
      root.querySelector('[data-protein-calculator]').setAttribute('aria-expanded', 'false');
    }
  }));
  listen(dialog, 'close', () => {
    if (disposed) return;
    document.body.classList.remove('pc-modal-open');
    if (returnToInline) {
      inline.append(layout);
      inline.hidden = false;
    }
    const target = returnFocus && returnFocus !== document.body && returnFocus.isConnected ? returnFocus : root.querySelector('[data-protein-calculator]');
    target?.focus({preventScroll: true});
  });
  function autoOpen() {
    if (disposed || wasSeen()) return;
    if (document.visibilityState === 'hidden' || document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]') || document.activeElement?.closest('input,select,textarea,[contenteditable="true"]')) {
      autoTimer = setTimeout(autoOpen, 2000);
      return;
    }
    openCalculator();
  }
  syncAge(); syncUnitVisibility(); updateCounters(); showStep(0, false);
  if (!wasSeen()) autoTimer = setTimeout(autoOpen, 3500);
  return () => {
    disposed = true;
    clearTimeout(autoTimer);
    controller.abort();
    if (dialog.open) dialog.close();
    document.body.classList.remove('pc-modal-open');
    dialog.append(layout);
    inline.hidden = true;
    root.querySelectorAll('[data-protein-calculator]').forEach(button => button.setAttribute('aria-expanded', 'false'));
  };
}
